/* import __COLOCATED_TEMPLATE__ from './usage.hbs'; */
/* RESPONSIBLE TEAM: team-phone */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import { tracked } from '@glimmer/tracking';
import type CallingUsageData from 'embercom/models/calling-usage-data';
import moment from 'moment-timezone';

interface Args {
  usages: CallingUsageData | null;
}

interface Signature {
  Args: Args;
}

export default class Usage extends Component<Signature> {
  @service declare appService: any;
  @service declare intl: IntlService;
  @tracked callingUsages: CallingUsageData | null = null;

  constructor(owner: unknown, args: any) {
    super(owner, args);
    this.callingUsages = args.usages;
  }

  get getCommonColumns(): any[] {
    return [
      {
        label: this.intl.t('billing.usage.phone.sub_category'),
        valuePath: 'sub_category',
        isSortable: true,
      },
      {
        label: this.intl.t('billing.usage.phone.units-minutes'),
        valuePath: 'units',
        tooltip: this.intl.t('billing.usage.phone.units-minutes-tooltip'),
        isSortable: true,
      },
      {
        label: this.intl.t('billing.usage.phone.price'),
        valuePath: 'price_in_cent',
        tooltip: this.intl.t('billing.usage.phone.price-tooltip'),
        isSortable: true,
        type: 'number',
      },
    ];
  }

  get getCallsColumns(): any[] {
    return [
      {
        label: this.intl.t('billing.usage.phone.iso_country'),
        valuePath: 'iso_country',
        isSortable: true,
        maxWidth: '250px',
        width: '250px',
        type: 'avatar-with-text',
      },
      {
        label: this.intl.t('billing.usage.phone.sub_category'),
        valuePath: 'sub_category',
        isSortable: true,
      },
      {
        label: this.intl.t('billing.usage.phone.units-minutes'),
        valuePath: 'units',
        tooltip: this.intl.t('billing.usage.phone.units-minutes-tooltip'),
        isSortable: true,
      },
      {
        label: this.intl.t('billing.usage.phone.price'),
        valuePath: 'price_in_cent',
        tooltip: this.intl.t('billing.usage.phone.price-tooltip'),
        isSortable: true,
        type: 'number',
      },
    ];
  }

  get getPhoneNumberColumns(): any[] {
    return [
      {
        label: this.intl.t('billing.usage.phone.name'),
        valuePath: 'name',
        isSortable: true,
        maxWidth: '250px',
        width: '250px',
      },
      {
        label: this.intl.t('billing.usage.phone.iso_country'),
        valuePath: 'iso_country',
        isSortable: true,
        maxWidth: '250px',
        width: '250px',
        type: 'avatar-with-text',
      },
      {
        label: this.intl.t('billing.usage.phone.phone_type'),
        valuePath: 'sub_category',
        isSortable: true,
      },
      {
        label: this.intl.t('billing.usage.phone.phone_number'),
        valuePath: 'phone_number',
        isSortable: true,
      },
      {
        label: this.intl.t('billing.usage.phone.price'),
        valuePath: 'price_in_cent',
        isSortable: true,
        type: 'number',
      },
    ];
  }

  get phoneNumberPrice(): number {
    return (
      this.callingUsages?.phone_numbers.reduce((acc, row) => acc + row.price_in_cent, 0) ?? 0 / 100
    );
  }

  get inboundCallPrice(): number {
    return (
      this.callingUsages?.inbound_calls.reduce((acc, row) => acc + row.price_in_cent, 0) ?? 0 / 100
    );
  }

  get outboundCallPrice(): number {
    return (
      this.callingUsages?.outbound_calls.reduce((acc, row) => acc + row.price_in_cent, 0) ?? 0 / 100
    );
  }

  get recordingPrice(): number {
    return (
      this.callingUsages?.recordings.reduce((acc, row) => acc + row.price_in_cent, 0) ?? 0 / 100
    );
  }

  get messengerCallPrice(): number {
    return (
      this.callingUsages?.messenger_calls.reduce((acc, row) => acc + row.price_in_cent, 0) ??
      0 / 100
    );
  }

  get billingPeriodDate() {
    let humanizedDate = 'MMMM DD, YYYY';
    return `${moment.tz(this.callingUsages?.from_date, 'UTC').format(humanizedDate)} - ${moment
      .tz(this.callingUsages?.to_date, 'UTC')
      .format(humanizedDate)}`;
  }

  get billingTotal() {
    return (
      this.phoneNumberPrice +
      this.inboundCallPrice +
      this.outboundCallPrice +
      this.recordingPrice +
      this.messengerCallPrice
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Billing::Usage::Phone::Usage': typeof Usage;
    'billing/usage/phone/usage': typeof Usage;
  }
}
